
import React from "react"
import { useFormik } from "formik"
import { observer } from "mobx-react-lite"
import { useTheme } from "~/ui/styled"
import { Pressable, ScrollView } from "~/ui/primitives"
import { dismissKeyboardOnPress } from "~utils/keyboard"
import { SafeAreaView } from "~/ui/safe-area"
import { useSafeAreaInsets } from "~/ui/safe-area"
import { ColumnFlex, Text, Button, BetweenFlex } from "~components/El"
import { TextField } from "~components/InputField"
import { Header } from "~components/header"
import useNavigation from "~hooks/useNavigation"
import { useStores } from "~models"
import { useModal } from "~hooks/useModal"
import { Event, EventNames, UserUpdateData, FLAG_ACK } from "~proto"
import { globalToast } from "~utils"
import { UpdateUserDisplayNameSchema } from "~schema"
import { scrollViewFillStyle } from "~/ui/scroll-layout"

export interface UpdateCurrentUserDisplayNameFormProps {
  onDismiss: () => void
}

const UpdateCurrentUserDisplayName: React.FC<UpdateCurrentUserDisplayNameFormProps> = ({
  onDismiss,
}) => {
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const navigation = useNavigation()
  const { eventStore, userStore } = useStores()
  const currentName = userStore.currentUser.name
  const { Dialog } = useModal()
  const dismiss = () => {
    if (onDismiss) {
      onDismiss()
    } else {
      navigation.goBack()
    }
  }

  const publishUpdate = (name: string) => {
    const evt = new Event<Partial<UserUpdateData>>(EventNames.USER_UPDATE, {
      name,
    })
    eventStore.publish(evt, { flags: FLAG_ACK })
    globalToast.show("昵称修改成功")
    dismiss()
  }

  const formik = useFormik({
    initialValues: { name: currentName },
    validationSchema: UpdateUserDisplayNameSchema,
    onSubmit: (values) => {
        Dialog.show({
          title: "提示",
          content: "保存本次编辑?",
          onConfirm: () => publishUpdate(values.name.trim()),
          onCancel: dismiss,
        })
    },
  })

  const { values, errors, touched, setFieldValue, setFieldTouched, handleSubmit, dirty, isValid } =
    formik

  const submitDisabled = !dirty || !isValid

  return (
    <SafeAreaView edges={["top", "bottom"]} style={{ flex: 1, minHeight: 0, overflow: "hidden", paddingTop: insets.top ,width: "100%"}}>
      <BetweenFlex width="100%" onClick={dismissKeyboardOnPress}>
        <Header
          width="100%"
          headerText="修改昵称"
          onLeftPress={dismiss}
          headerRight={
            <Button
              width="auto"
              height="30px"
              padding="0 12px"
              borderRadius="4px"
              bg={theme.colors.primary}
              onClick={() => handleSubmit()}
              disabled={submitDisabled}
            >
              <Text color={theme.colors.textLight} fontSize="14px">
                完成
              </Text>
            </Button>
          }
        />
      </BetweenFlex   >
      <ScrollView
        style={scrollViewFillStyle}
        keyboardShouldPersistTaps="never"
        keyboardDismissMode="on-drag"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingBottom: insets.bottom + 16,
        }}
      >
        <ColumnFlex width="100%" padding="12px" bg={theme.colors.blockBackground}>
          <TextField
            label="昵称"
            value={values.name}
            placeholder="请输入昵称"
            keyboardType="default"
            maxLength={32}
            onChangeText={(v) => setFieldValue("name", v)}
            onBlur={() => setFieldTouched("name", true)}
            error={touched.name && typeof errors.name === "string" ? errors.name : false}
          />
        </ColumnFlex>
      </ScrollView>
    </SafeAreaView>
  )
}

export default observer(UpdateCurrentUserDisplayName)
